import React, { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Link,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  BookmarkAddOutlined as SaveIcon,
  CheckCircleOutline as SavedIcon,
} from '@mui/icons-material';
import { answersApi, profileApi, templatesApi } from '../../services/api';
import {
  ApplicationAnswer,
  CanonicalQuestion,
  CUSTOM_RESPONSE_CATEGORY,
  FormTemplate,
  Profile,
  ProfilePatch,
  UserAnswer,
} from '../../types';
import { errorMessage } from '../../utils/errors';
import {
  AnswerHeading,
  SaveAction,
  SaveLookups,
  SavePlan,
  describeAnswer,
  formatAnswerValue,
  groupAnswersByFilledBy,
  planSave,
} from '../../utils/applicationAnswers';
import ConfirmDialog from '../common/ConfirmDialog';

interface Props {
  answers: ApplicationAnswer[];
  notify: (message: string, severity?: 'success' | 'error') => void;
}

interface PendingReplace {
  answerId: string;
  action: SaveAction;
  replaces: string;
}

const GROUP_COLOR: Record<string, 'info' | 'success' | 'default'> = {
  user: 'info',
  auto: 'success',
  skipped: 'default',
};

const wrapSx = { whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' } as const;

/** Values longer than this start collapsed, with a control to show the rest. */
const LONG_VALUE_CHARS = 320;
const LONG_VALUE_LINES = 5;

const newValueOf = (action: SaveAction): string =>
  action.target === 'answer' ? formatAnswerValue(action.value) : action.value;

const ApplicationAnswers: React.FC<Props> = ({ answers, notify }) => {
  const [questions, setQuestions] = useState<Record<string, CanonicalQuestion> | null>(null);
  const [savedAnswers, setSavedAnswers] = useState<Record<string, UserAnswer> | null>(null);
  const [customAnswers, setCustomAnswers] = useState<FormTemplate[] | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [lookupsLoading, setLookupsLoading] = useState(false);
  const [lookupsFailed, setLookupsFailed] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  const [savingId, setSavingId] = useState<string | null>(null);
  const [pending, setPending] = useState<PendingReplace | null>(null);

  const hasAnswers = answers.length > 0;
  // The saved-answer, custom-answer and profile lookups only matter when
  // there is something the user typed that could be saved.
  const hasTyped = useMemo(
    () => answers.some((a) => a.filled_by === 'user' && (a.value || '').trim() !== ''),
    [answers]
  );

  useEffect(() => {
    if (!hasAnswers) return undefined;
    let cancelled = false;
    setLookupsLoading(true);
    setLookupsFailed(false);

    (async () => {
      const [questionsResult, answersResult, customResult, profileResult] = await Promise.allSettled([
        answersApi.questions(),
        hasTyped ? answersApi.list() : Promise.resolve(null),
        hasTyped ? templatesApi.list(CUSTOM_RESPONSE_CATEGORY) : Promise.resolve(null),
        hasTyped ? profileApi.get() : Promise.resolve(null),
      ]);
      if (cancelled) return;

      if (questionsResult.status === 'fulfilled') {
        const map: Record<string, CanonicalQuestion> = {};
        questionsResult.value.forEach((q) => {
          map[q.key] = q;
        });
        setQuestions(map);
      }
      if (answersResult.status === 'fulfilled' && answersResult.value) {
        const map: Record<string, UserAnswer> = {};
        answersResult.value.forEach((a) => {
          map[a.question_key] = a;
        });
        setSavedAnswers(map);
      }
      if (customResult.status === 'fulfilled' && customResult.value) {
        // Same defensive filter as the answer library's custom answers tab.
        setCustomAnswers(customResult.value.filter((t) => t.category === CUSTOM_RESPONSE_CATEGORY));
      }
      if (profileResult.status === 'fulfilled' && profileResult.value) {
        setProfile(profileResult.value);
      }
      setLookupsFailed(
        [questionsResult, answersResult, customResult, profileResult].some(
          (r) => r.status === 'rejected'
        )
      );
      setLookupsLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [hasAnswers, hasTyped, reloadKey]);

  const lookups = useMemo<SaveLookups>(
    () => ({ questions, savedAnswers, customAnswers, profile }),
    [questions, savedAnswers, customAnswers, profile]
  );

  const groups = useMemo(() => groupAnswersByFilledBy(answers), [answers]);

  const runSave = async (answerId: string, action: SaveAction) => {
    setSavingId(answerId);
    try {
      if (action.target === 'answer') {
        // Same payload shape the answer library sends; the value is already
        // typed for the question (see toAnswerValue).
        const saved = await answersApi.put(action.questionKey, { value: action.value, verified: true });
        setSavedAnswers((prev) => ({ ...(prev ?? {}), [action.questionKey]: saved }));
        notify(`Saved "${action.label}" to your answers.`);
      } else if (action.target === 'profile') {
        const patch: ProfilePatch = {};
        patch[action.field] = action.value;
        const updated = await profileApi.patch(patch);
        setProfile(updated);
        notify(`Saved ${action.label} to your profile.`);
      } else {
        const saved =
          action.existingId !== null
            ? await templatesApi.patch(action.existingId, { value: action.value })
            : await templatesApi.create({
                category: CUSTOM_RESPONSE_CATEGORY,
                question: action.question,
                value: action.value,
              });
        setCustomAnswers((prev) => [saved, ...(prev ?? []).filter((t) => t.id !== saved.id)]);
        notify('Saved to your custom answers.');
      }
    } catch (e) {
      notify(
        errorMessage(
          e,
          action.target === 'profile' ? 'Could not update your profile.' : 'Could not save the answer.'
        ),
        'error'
      );
    } finally {
      setSavingId(null);
      setPending(null);
    }
  };

  const requestSave = (answer: ApplicationAnswer, plan: SavePlan) => {
    if (plan.kind !== 'save' || savingId !== null) return;
    const answerId = String(answer.id);
    if (plan.replaces) {
      setPending({ answerId, action: plan.action, replaces: plan.replaces });
    } else {
      runSave(answerId, plan.action);
    }
  };

  if (!hasAnswers) {
    return (
      <Typography variant="body2" color="text.secondary">
        No answers were recorded for this application.
      </Typography>
    );
  }

  return (
    <Box>
      <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 1, mb: 2 }}>
        {groups.map((group) => (
          <Chip
            key={group.value}
            size="small"
            variant="outlined"
            color={GROUP_COLOR[group.value] ?? 'default'}
            label={`${group.label}: ${group.answers.length}`}
          />
        ))}
      </Stack>

      {lookupsFailed && (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={() => setReloadKey((k) => k + 1)}>
              Retry
            </Button>
          }
        >
          Some of your saved answers could not be loaded, so question names and saving may be
          unavailable here.
        </Alert>
      )}

      <Stack spacing={3}>
        {groups
          .filter((group) => group.answers.length > 0)
          .map((group) => (
            <Box key={group.value}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="subtitle1" component="h3" fontWeight={600}>
                  {group.label}
                </Typography>
                <Chip label={group.answers.length} size="small" sx={{ height: 20 }} />
              </Stack>
              {group.description && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {group.description}
                </Typography>
              )}
              <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, px: 2 }}>
                {group.answers.map((answer) => {
                  const answerId = String(answer.id);
                  return (
                    <AnswerRow
                      key={answerId}
                      answer={answer}
                      heading={describeAnswer(answer, questions)}
                      plan={planSave(answer, lookups)}
                      lookupsLoading={lookupsLoading}
                      saving={savingId === answerId}
                      disabled={savingId !== null}
                      onSave={requestSave}
                    />
                  );
                })}
              </Box>
            </Box>
          ))}
      </Stack>

      <ConfirmDialog
        open={Boolean(pending)}
        title={
          pending?.action.target === 'profile'
            ? `Replace ${pending.action.label} in your profile?`
            : 'Replace your saved answer?'
        }
        message={
          pending && (
            <Stack spacing={1.5}>
              <Typography variant="body2" color="text.secondary">
                {pending.action.target === 'profile'
                  ? 'Your profile already has a different value. The extension will fill the new one from now on.'
                  : pending.action.target === 'answer'
                  ? `You already have a different saved answer for "${pending.action.label}". The extension will fill the new one from now on.`
                  : 'You already have a different custom answer for this prompt. The extension will suggest the new one from now on.'}
              </Typography>
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  Current
                </Typography>
                <Typography variant="body2" sx={wrapSx}>
                  {pending.replaces}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  New
                </Typography>
                <Typography variant="body2" sx={wrapSx}>
                  {newValueOf(pending.action)}
                </Typography>
              </Box>
            </Stack>
          )
        }
        confirmLabel="Replace"
        loading={savingId !== null}
        onConfirm={() => pending && runSave(pending.answerId, pending.action)}
        onCancel={() => setPending(null)}
      />
    </Box>
  );
};

interface AnswerRowProps {
  answer: ApplicationAnswer;
  heading: AnswerHeading;
  plan: SavePlan;
  lookupsLoading: boolean;
  saving: boolean;
  disabled: boolean;
  onSave: (answer: ApplicationAnswer, plan: SavePlan) => void;
}

const SAVED_TOOLTIP: Record<string, string> = {
  answer: 'This value is in your answer library.',
  profile: 'This value is in your profile.',
  custom: 'This value is in your custom answers.',
};

const AnswerRow: React.FC<AnswerRowProps> = ({
  answer,
  heading,
  plan,
  lookupsLoading,
  saving,
  disabled,
  onSave,
}) => {
  const [expanded, setExpanded] = useState(false);
  const value = answer.value || '';
  const isLong = value.length > LONG_VALUE_CHARS || value.split('\n').length > LONG_VALUE_LINES;

  const renderAction = () => {
    switch (plan.kind) {
      case 'save': {
        const toProfile = plan.action.target === 'profile';
        return (
          <Tooltip
            title={
              toProfile
                ? 'Update your profile with this value so the extension fills it next time.'
                : 'Keep this answer so the extension can reuse it next time.'
            }
          >
            <span>
              <Button
                size="small"
                variant="outlined"
                startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
                disabled={disabled}
                onClick={() => onSave(answer, plan)}
                sx={{ whiteSpace: 'nowrap' }}
              >
                {toProfile ? 'Save to profile' : 'Save to my answers'}
              </Button>
            </span>
          </Tooltip>
        );
      }
      case 'saved':
        if (plan.target === 'answer' && !plan.verified) {
          return (
            <Tooltip title="This value is in your answer library but not verified, so it is suggested rather than filled automatically. Open the answer library to verify it.">
              <Chip
                component={RouterLink}
                to="/answers"
                clickable
                icon={<SavedIcon />}
                label="Saved, not verified"
                size="small"
                variant="outlined"
              />
            </Tooltip>
          );
        }
        return (
          <Tooltip title={SAVED_TOOLTIP[plan.target]}>
            <Chip icon={<SavedIcon />} label="Saved" size="small" color="success" variant="outlined" />
          </Tooltip>
        );
      case 'blocked':
        if (plan.target === 'answer') {
          return (
            <Tooltip title={plan.reason}>
              <Button component={RouterLink} to="/answers" size="small" sx={{ whiteSpace: 'nowrap' }}>
                Set in answer library
              </Button>
            </Tooltip>
          );
        }
        return (
          <Tooltip title={plan.reason}>
            <span>
              <Button size="small" variant="outlined" startIcon={<SaveIcon />} disabled sx={{ whiteSpace: 'nowrap' }}>
                Save to profile
              </Button>
            </span>
          </Tooltip>
        );
      case 'unavailable':
        return lookupsLoading ? <CircularProgress size={16} aria-label="Checking your saved answers" /> : null;
      default:
        return plan.note ? (
          <Link component={RouterLink} to="/profile" variant="caption" color="text.secondary">
            {plan.note}
          </Link>
        ) : null;
    }
  };

  const action = renderAction();

  return (
    <Box
      sx={{
        py: 1.5,
        borderBottom: 1,
        borderColor: 'divider',
        '&:last-of-type': { borderBottom: 0 },
      }}
    >
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={{ xs: 1, sm: 2 }}
        alignItems="flex-start"
        justifyContent="space-between"
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" fontWeight={600} sx={{ overflowWrap: 'anywhere' }}>
            {heading.title}
          </Typography>
          {heading.matchedLabel && (
            <Typography variant="caption" color="text.secondary" display="block">
              JobFlo question: {heading.matchedLabel}
            </Typography>
          )}
          {value ? (
            <>
              <Typography
                variant="body2"
                sx={{
                  ...wrapSx,
                  mt: 0.5,
                  ...(isLong && !expanded
                    ? {
                        display: '-webkit-box',
                        WebkitLineClamp: LONG_VALUE_LINES,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }
                    : {}),
                }}
              >
                {value}
              </Typography>
              {isLong && (
                <Button
                  size="small"
                  onClick={() => setExpanded((open) => !open)}
                  aria-expanded={expanded}
                  sx={{ mt: 0.5, ml: -0.5, minWidth: 0 }}
                >
                  {expanded ? 'Show less' : 'Show full answer'}
                </Button>
              )}
            </>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              No value recorded
            </Typography>
          )}
        </Box>
        {action && <Box sx={{ flexShrink: 0 }}>{action}</Box>}
      </Stack>
    </Box>
  );
};

export default ApplicationAnswers;
